import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PinClient, PinDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const apiClient = new PinClient(API_BASE_URL, axiosInstance);

// Query hook - Get all pins for dropdowns (cached 4 hours)
export const usePins = () => {
  return useQuery({
    queryKey: ['pins'],
    queryFn: async () => {
      const response = await apiClient.searchPins({
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });
};

// Direct API search (no caching) - for admin tables with server-side paging
export const usePinSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchPins({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Query hook - Get single pin by ID
export const usePinById = (id: string) => {
  return useQuery({
    queryKey: ['pins', id],
    queryFn: async () => {
      const response = await apiClient.getPinById(id);
      return response?.data;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });
};

// Mutation hook - Create pin
export const useCreatePin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PinDTO) => {
      await apiClient.createPin(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pins'] });
    },
  });
};

// Mutation hook - Update pin
export const useUpdatePin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PinDTO }) => {
      await apiClient.updatePin(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pins'] });
    },
  });
};

// Mutation hook - Delete pin
export const useDeletePin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.deletePin(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pins'] });
    },
  });
};
